import React, { useState, useEffect, useRef } from 'react';
import { X, Eye, Clock, Star, Calendar, Tag, ThumbsUp, User, Bookmark, BookmarkCheck, HelpCircle, Minimize2, Expand } from 'lucide-react';
import toast from 'react-hot-toast';
import { useWatchlist } from '../context/WatchlistContext';
import { useAuth } from '../AuthContext';
import { ManhwaRecap } from '../types';
import { formatDuration, formatViews, formatDateShort, formatLikes, parseGenres, parseTags } from '../utils/formatters';
import { BasicPlayer } from './BasicPlayer';
import { cn } from '../lib/utils';

interface ShortcutItem {
  key: string;
  description: string;
}

const PLAYER_SHORTCUTS: ShortcutItem[] = [
  { key: "F", description: "TOGGLE FULLSCREEN" },
  { key: "M", description: "MUTE / UNMUTE" },
  { key: "SPACE", description: "PLAY / PAUSE" },
  { key: "← →", description: "SEEK ±10 SECONDS" },
  { key: "↑ ↓", description: "VOLUME ±5" },
  { key: "ESC", description: "EXIT FULLSCREEN / CLOSE" },
];

interface KeyboardHelpOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

// Hook to detect mobile devices
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      // More comprehensive mobile detection
      const width = window.innerWidth;
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice =
        width < 768 ||
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent) ||
        (width < 1024 && /mobile/i.test(userAgent));

      console.log('Enhanced mobile detection:', {
        width,
        userAgent: userAgent.substring(0, 100) + '...',
        isMobileDevice,
        widthCheck: width < 768,
        userAgentCheck: /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent),
        tabletCheck: width < 1024 && /mobile/i.test(userAgent)
      });
      setIsMobile(isMobileDevice);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    window.addEventListener('orientationchange', checkMobile);
    return () => {
      window.removeEventListener('resize', checkMobile);
      window.removeEventListener('orientationchange', checkMobile);
    };
  }, []);

  return isMobile;
};

const KeyboardHelpOverlay = ({ isOpen, onClose }: KeyboardHelpOverlayProps) => {
  // Handle ESC key to close overlay
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/75 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-sm w-full mx-4 shadow-2xl border border-gray-200 dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            KEYBOARD SHORTCUTS
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Shortcuts List */}
        <div className="space-y-3 mb-6">
          {PLAYER_SHORTCUTS.map((shortcut) => (
            <div
              key={shortcut.key}
              className="flex items-center justify-between"
            >
              <span className="font-mono text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-gray-700 dark:text-gray-300">
                {shortcut.key}
              </span>
              <span className="text-sm text-gray-600 dark:text-gray-400 flex-1 text-right ml-3">
                {shortcut.description}
              </span>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="pt-3 border-t border-gray-200 dark:border-gray-700 text-center">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Click outside or press ESC to close
          </span>
        </div>
      </div>
    </div>
  );
};


interface RecapModalProps {
  recap: ManhwaRecap | null;
  isOpen: boolean;
  onClose: () => void;
}

export const RecapModal: React.FC<RecapModalProps> = ({ recap, isOpen, onClose }) => {
  console.log('RecapModal render:', { recap: !!recap, isOpen, hasRecap: !!recap, recapId: recap?.id });

  const { watchlist, addToWatchlist, removeFromWatchlist } = useWatchlist();
  const { isAuthenticated } = useAuth();
  const hasManagedOverflow = useRef(false);
  const [showHelp, setShowHelp] = useState(false);
  const [isMiniPlayer, setIsMiniPlayer] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const isMobile = useIsMobile();
  console.log('Mobile detection result:', isMobile);

  // Auto-enable mini-player on mobile when modal opens
  useEffect(() => {
    console.log('Mini-player effect:', { isOpen, isMobile, currentMiniPlayer: isMiniPlayer });
    if (isOpen && isMobile) {
      console.log('Setting mini-player to true');
      setIsMiniPlayer(true);
    } else if (!isMobile) {
      console.log('Setting mini-player to false (not mobile)');
      setIsMiniPlayer(false);
    }
  }, [isOpen, isMobile]);

  // Track fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Handle back button for mini-player
  useEffect(() => {
    // Delay popstate handling to avoid interfering with initial modal opening
    const timeoutId = setTimeout(() => {
      const currentUrl = window.location.href;

      const handlePopState = () => {
        if (isMobile && isMiniPlayer && !isOpen) {
          // Close mini-player when back button is pressed
          setIsMiniPlayer(false);
          // Replace history to prevent navigation
          window.history.replaceState(null, '', currentUrl);
        }
      };

      if (isMobile && isMiniPlayer && !isOpen) {
        window.addEventListener('popstate', handlePopState);
      }

      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }, 100); // Small delay to avoid interference

    return () => clearTimeout(timeoutId);
  }, [isMobile, isMiniPlayer, isOpen]);

  const handleMinimize = () => {
    setIsMiniPlayer(!isMiniPlayer);
  };

  // Prevent background scrolling and focus issues when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';

      // Prevent focus on elements outside the modal
      const handleFocusIn = (e: FocusEvent) => {
        const target = e.target as HTMLElement;
        const modalContent = document.querySelector('[data-modal-content]');
        if (modalContent && !modalContent.contains(target)) {
          // Prevent focus on elements outside the modal
          e.preventDefault();
          target.blur();
        }
      };

      document.addEventListener('focusin', handleFocusIn);
      return () => document.removeEventListener('focusin', handleFocusIn);
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isOpen]);

  if (!isOpen || !recap) {
    console.log('RecapModal early return:', { isOpen, hasRecap: !!recap, recapId: recap?.id });
    return null;
  }

  const isBookmarked = watchlist.includes(recap.id);

  const handleBookmark = async () => {
    if (!isAuthenticated) {
      toast.error('Please log in to save items to your watchlist.');
      return;
    }
    try {
      if (isBookmarked) {
        await removeFromWatchlist(recap.id);
        toast.success('Removed from watchlist');
      } else {
        await addToWatchlist(recap.id);
        toast.success('Added to watchlist');
      }
    } catch (error) {
      console.error('Failed to update watchlist:', error);
      toast.error('Failed to update watchlist. Please try again.');
    }
  };

  const genres = parseGenres(recap.genres ?? null);
  const tags = parseTags(recap.tags ?? null);
  const allTags = [...genres, ...tags];

  // Simple fullscreen exit controls
  const FullscreenControls = () => (
    <div className="fixed top-4 right-4 z-[100]">
      <button
        onClick={() => document.exitFullscreen().catch(console.error)}
        className="p-3 bg-black bg-opacity-50 rounded-full hover:bg-opacity-70 transition-all duration-200"
        title="Exit Fullscreen"
      >
        <X className="w-6 h-6 text-white" />
      </button>
    </div>
  );

  // Mini-player mode for mobile (or regular modal if mobile detection fails)
  console.log('Mini-player condition check:', { isMobile, isMiniPlayer, shouldShowMiniPlayer: isMobile && isMiniPlayer });
  if (isMobile && isMiniPlayer) {
    console.log('Rendering mini-player mode');
    return (
      <>
        {/* Backdrop to prevent interaction with background content */}
        <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" />

        {/* Mini-player fixed at top */}
        <div className="fixed top-0 left-0 right-0 z-50 bg-black shadow-lg">
          <div className="relative">
            <div className="w-full h-[650px] bg-black" style={{ aspectRatio: '16/9' }}>
              <BasicPlayer
                initialVideoId={recap.video_id}
                playlist={[{
                  id: recap.video_id,
                  title: recap.series_name || recap.title,
                  channel: recap.channel_name,
                  thumbnail: recap.thumbnail_url,
                  duration: formatDuration(recap.duration)
                }]}
                autoPlay={true}
                onClose={onClose}
                helpOverlayOpen={showHelp}
                className="w-full h-full"
              />
            </div>
            <div className="absolute top-2 right-2 flex gap-2">
              <button
                onClick={async () => {
                  // Trigger browser fullscreen with landscape orientation
                  if (!document.fullscreenElement) {
                    try {
                      // Request fullscreen first
                      await document.documentElement.requestFullscreen();

                      // Then try to lock to landscape orientation
                      if (screen.orientation && screen.orientation.lock) {
                        try {
                          await screen.orientation.lock('landscape');
                        } catch (orientationError) {
                          console.log('Orientation lock not supported or failed:', orientationError);
                          // Continue anyway - fullscreen still works
                        }
                      }
                    } catch (error) {
                      console.error('Failed to enter fullscreen:', error);
                    }
                  } else {
                    // Exit fullscreen
                    try {
                      await document.exitFullscreen();

                      // Try to unlock orientation
                      if (screen.orientation && screen.orientation.unlock) {
                        screen.orientation.unlock();
                      }
                    } catch (error) {
                      console.error('Failed to exit fullscreen:', error);
                    }
                  }
                }}
                className="p-2 bg-black bg-opacity-50 rounded-full hover:bg-opacity-70 transition-all duration-200"
                title="Landscape Fullscreen"
              >
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h18v18H3V3z" />
                </svg>
              </button>
              <button
                onClick={handleMinimize}
                className="p-2 bg-black bg-opacity-50 rounded-full hover:bg-opacity-70 transition-all duration-200"
                title="Minimize"
              >
                <Minimize2 className="w-4 h-4 text-white" />
              </button>
              <button
                onClick={onClose}
                className="p-2 bg-black bg-opacity-50 rounded-full hover:bg-opacity-70 transition-all duration-200"
                title="Close"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
            <div className="p-3 bg-gradient-to-t from-black to-transparent">
              <h3 className="text-white font-semibold text-sm truncate">
                {recap.series_name || recap.title}
              </h3>
              <p className="text-gray-300 text-xs">{recap.channel_name}</p>
            </div>
          </div>
        </div>

        {/* Scrollable content below mini-player - REMOVED */}
        {/* <div className="pt-[650px] min-h-screen bg-white dark:bg-gray-900">
          <div className="max-w-4xl mx-auto p-4">
            Recap details, stats, tags, watchlist button, and keyboard shortcuts removed
          </div>
        </div> */}

        {/* Keyboard help overlay */}
        <KeyboardHelpOverlay isOpen={showHelp} onClose={() => setShowHelp(false)} />

        {/* Fullscreen controls overlay */}
        {isFullscreen && <FullscreenControls />}
      </>
    );
  }

  console.log('Rendering regular modal mode, isMobile:', isMobile, 'isMiniPlayer:', isMiniPlayer);
  console.log('Regular modal props:', { recap: !!recap, isOpen });
  return (
    <div
      className="fixed inset-0 bg-black/75 dark:bg-black/75 flex items-center justify-center z-50 p-4"
      tabIndex={-1}
      onClick={(e) => {
        // Prevent focus changes when clicking the backdrop
        e.preventDefault();
        e.stopPropagation();
        // Prevent any focus changes
        if (document.activeElement && document.activeElement !== document.body) {
          (document.activeElement as HTMLElement).blur();
        }
        // Keep focus on the backdrop to prevent other elements from getting focus
        (e.target as HTMLElement).focus();
      }}
    >
      <div
        className={cn(
          "bg-white dark:bg-gray-800 rounded-xl w-full max-h-[95vh] overflow-hidden shadow-2xl border border-gray-200 dark:border-gray-700 max-w-6xl flex flex-col",
          // Better mobile spacing
          "p-2 sm:p-6"
        )}
        data-modal-content
      >
        <div className="relative flex-shrink-0">
          <div className="w-full max-h-[80vh] md:max-h-[60vh]" style={{ aspectRatio: '16/9' }}>
            <BasicPlayer
              initialVideoId={recap.video_id}
              playlist={[{
                id: recap.video_id,
                title: recap.series_name || recap.title,
                channel: recap.channel_name,
                thumbnail: recap.thumbnail_url,
                duration: formatDuration(recap.duration)
              }]}
              autoPlay={true}
              onClose={onClose}
              helpOverlayOpen={showHelp}
              className="w-full h-full"
            />
          </div>
          <div className="absolute top-4 right-4 flex gap-2 z-10">
            {isMobile && (
              <button
                onClick={handleMinimize}
                className="p-2 bg-black bg-opacity-50 rounded-full hover:bg-opacity-70 transition-all duration-200"
              >
                <Minimize2 className="w-6 h-6 text-white" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 bg-black bg-opacity-50 rounded-full hover:bg-opacity-70 transition-all duration-200"
            >
              <X className="w-6 h-6 text-white" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2" title={recap.series_name || recap.title}>
            {recap.series_name || recap.title}
          </h2>

          {recap.series_name && (
            <p className="text-gray-600 dark:text-gray-400 mb-6 line-clamp-2" title={recap.title}>
              {recap.title}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-6 mb-6">
            <div className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Views</p>
                <p className="text-gray-900 dark:text-white font-semibold">{formatViews(recap.views)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <ThumbsUp className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Likes</p>
                <p className="text-gray-900 dark:text-white font-semibold">{formatLikes(recap.like_count || 0)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Duration</p>
                <p className="text-gray-900 dark:text-white font-semibold">{formatDuration(recap.duration)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Published</p>
                <p className="text-gray-900 dark:text-white font-semibold">{formatDateShort(recap.published_at)}</p>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <User className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              Channel
            </h3>
            <p className="text-purple-600 dark:text-purple-400 font-medium">{recap.channel_name}</p>
          </div>

          {allTags.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <Tag className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                Tags & Genres
              </h3>
              <div className="flex flex-wrap gap-2">
                {allTags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm rounded-md"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {recap.chapters_covered && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Chapters Covered</h3>
              <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
                <span className="text-gray-700 dark:text-gray-300">{recap.chapters_covered}</span>
              </div>
            </div>
          )}

          <div className="flex gap-4">
            <button
              onClick={handleBookmark}
              title="Save to Favourites"
              className="py-3 px-6 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white"
            >
              {isBookmarked ? <BookmarkCheck className="w-5 h-5" /> : <Bookmark className="w-5 h-5" />}
              {isBookmarked ? 'Saved' : 'Save'}
            </button>
            <button
              onClick={() => setShowHelp(true)}
              title="Keyboard Shortcuts"
              className="px-4 py-3 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
            >
              <HelpCircle className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Fullscreen controls overlay */}
      {isFullscreen && <FullscreenControls />}

      {/* Keyboard Help Overlay */}
      <KeyboardHelpOverlay
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
      />
    </div>
  );
};